import React, { useMemo, useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import {
  useGetDietAssessorLogbooksQuery,
  useUnassignDietAssessorLogbookMutation,
} from "../api/logbookDietApi";
import DataTable from "../components/DataTable";
import {
  ArrowLeft,
  RefreshCw,
  Eye,
  UserMinus,
  User,
  ShieldAlert,
  Search,
  X,
  Award,
  Briefcase,
  CheckCircle,
} from "lucide-react";

// ✅ Security & Permission Gates
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";

/**
 * Helper to extract error messages safely
 */
function safeMsg(x: any) {
  return String(
    x?.data?.message || x?.message || x?.error || "An unexpected error occurred"
  );
}

export default function DietAssessorLogbooksPage() {
  const navigate = useNavigate();
  const { id = "", memberId = "" } = useParams();

  // --- API HOOKS ---
  const { data, isLoading, error, refetch, isFetching } =
    useGetDietAssessorLogbooksQuery(
      { dietId: id, memberId },
      { skip: !id || !memberId }
    );

  const [unassign, { isLoading: removing }] =
    useUnassignDietAssessorLogbookMutation();

  // --- UI STATE ---
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [toast, setToast] = useState<{
    type: "ok" | "err";
    msg: string;
  } | null>(null);

  // Auto-clear toast
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // --- DATA MAPPING ---
  const diet = data?.diet;
  const member = data?.member;

  /** * ✅ FIX: Explicitly type the useMemo generic to guarantee an array.
   * This prevents "possibly undefined" TS errors in the rest of the component.
   */
  const rawLogbooks = useMemo<any[]>(() => {
    if (!data?.data) return [];
    return Array.isArray(data.data) ? data.data : [];
  }, [data]);

  // --- FILTERED DATA ---
  const filteredLogbooks = useMemo(() => {
    return rawLogbooks.filter((lb: any) => {
      const name =
        `${lb?.application?.surname} ${lb?.application?.other_names}`.toLowerCase();
      const refId = String(lb.id || "").toLowerCase();
      const matchesSearch =
        name.includes(searchTerm.toLowerCase()) ||
        refId.includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || lb.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [rawLogbooks, searchTerm, statusFilter]);

  // --- ACTIONS ---
  const handleRemove = async (logbookId: string) => {
    const confirmed = window.confirm(
      "CAUTION: Unassigning this logbook will immediately revoke this officer's grading access. Proceed?"
    );
    if (!confirmed) return;

    try {
      const res: any = await unassign({
        dietId: id,
        memberId,
        logbookId,
      }).unwrap();
      setToast({ type: "ok", msg: res?.message || "Assignment revoked." });
      refetch();
    } catch (e: any) {
      setToast({ type: "err", msg: safeMsg(e) });
    }
  };

  if (isLoading)
    return (
      <div className="p-20 text-center text-gray-400 font-black animate-pulse tracking-widest uppercase">
        Synchronizing Registry...
      </div>
    );

  if (error)
    return (
      <div className="m-10 p-10 border-2 border-red-100 bg-red-50 rounded-3xl text-red-700 flex flex-col items-center">
        <ShieldAlert className="w-12 h-12 mb-4 opacity-50" />
        <p className="font-black uppercase tracking-widest text-sm text-center">
          System Error: Failed to sync assignment ledger
        </p>
      </div>
    );

  return (
    <AccessGate perm={PERMS.logbook_diet.access}>
      <div className="space-y-6 pb-20 max-w-7xl mx-auto px-4">
        {/* TOP NAV */}
        <div className="flex items-center justify-between">
          <button
            onClick={() => navigate(`/logbook-diets/${id}`)}
            className="group flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-900 transition-all"
          >
            <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />{" "}
            Return to Diet Overview
          </button>
          <button
            onClick={() => refetch()}
            className="p-2 bg-white border border-gray-100 rounded-xl hover:bg-gray-50 shadow-sm transition-all"
          >
            <RefreshCw
              className={`w-4 h-4 text-gray-400 ${
                isFetching ? "animate-spin" : ""
              }`}
            />
          </button>
        </div>

        {/* OFFICER PROFILE HEADER */}
        <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm border-b-4 border-b-indigo-600">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 bg-gray-900 rounded-2xl flex items-center justify-center text-white shrink-0">
                <User className="w-8 h-8" />
              </div>
              <div>
                <h4 className="text-[10px] font-black text-indigo-600 uppercase tracking-[0.2em] mb-1">
                  Assigned Officer
                </h4>
                <h1 className="text-3xl font-black text-gray-900 tracking-tight leading-none uppercase italic">
                  {member?.surname} {member?.firstname}
                </h1>
                <div className="flex items-center gap-3 mt-2">
                  <span className="flex items-center gap-1.5 text-[10px] font-bold text-gray-400 uppercase">
                    <Briefcase className="w-3.5 h-3.5" />{" "}
                    {diet?.title || "Active Diet"}
                  </span>
                  <span className="w-1 h-1 bg-gray-300 rounded-full" />
                  <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest">
                    {rawLogbooks.length} Folders Allocated
                  </span>
                </div>
              </div>
            </div>

            <div className="flex gap-2">
              <div className="bg-green-50 border border-green-100 px-6 py-3 rounded-2xl text-center min-w-[100px]">
                <div className="text-[9px] font-black text-green-600 uppercase">
                  Assessed
                </div>
                <div className="text-xl font-black text-green-700">
                  {
                    rawLogbooks.filter((l: any) => l.status === "assessed")
                      .length
                  }
                </div>
              </div>
              <div className="bg-amber-50 border border-amber-100 px-6 py-3 rounded-2xl text-center min-w-[100px]">
                <div className="text-[9px] font-black text-amber-600 uppercase">
                  Pending
                </div>
                <div className="text-xl font-black text-amber-700">
                  {
                    rawLogbooks.filter((l: any) => l.status !== "assessed")
                      .length
                  }
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FEEDBACK TOAST */}
        {toast && (
          <div
            className={`p-4 rounded-2xl border flex items-center justify-between animate-in slide-in-from-top-2 ${
              toast.type === "ok"
                ? "bg-green-50 border-green-100 text-green-800"
                : "bg-red-50 border-red-100 text-red-800"
            }`}
          >
            <span className="flex items-center gap-3 font-bold text-sm">
              {toast.type === "ok" ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <ShieldAlert className="w-4 h-4" />
              )}
              {toast.msg}
            </span>
            <button onClick={() => setToast(null)}>
              <X className="w-4 h-4 opacity-50" />
            </button>
          </div>
        )}

        {/* WORKLOAD SEARCH & TABLE */}
        <div className="bg-white border border-gray-100 rounded-3xl shadow-sm overflow-hidden">
          <div className="p-6 border-b border-gray-50 flex flex-col md:flex-row justify-between items-center gap-4">
            <div>
              <h2 className="text-xl font-black text-gray-900 tracking-tight leading-none">
                Officer Workload
              </h2>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">
                Manage individual case assignments
              </p>
            </div>

            <div className="flex flex-1 max-w-xl gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300" />
                <input
                  type="text"
                  placeholder="Filter candidate name..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-transparent rounded-xl text-xs font-bold outline-none focus:bg-white focus:border-indigo-500 transition-all"
                />
              </div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-4 py-2 bg-gray-50 border border-transparent rounded-xl text-[10px] font-black uppercase outline-none focus:bg-white focus:border-indigo-500 transition-all"
              >
                <option value="all">Global Status</option>
                <option value="assessed">Assessed</option>
                <option value="submitted">Pending</option>
              </select>
            </div>
          </div>

          <DataTable
            data={filteredLogbooks}
            columns={[
              {
                key: "candidate",
                header: "Candidate Info",
                cell: (lb: any) => (
                  <div className="py-1">
                    <div className="font-bold text-gray-900 text-sm italic">
                      {lb?.application?.surname} {lb?.application?.other_names}
                    </div>
                    <div className="text-[10px] text-gray-400 font-bold uppercase tracking-tight">
                      Ref: {lb.id?.substring(0, 8)}
                    </div>
                  </div>
                ),
              },
              {
                key: "stage",
                header: "Phase",
                cell: (lb: any) => (
                  <span className="text-[10px] font-black text-gray-500 bg-gray-100 px-2.5 py-1 rounded-md border border-gray-200">
                    STAGE {lb.stage}
                  </span>
                ),
              },
              {
                key: "status",
                header: "Workflow Status",
                cell: (lb: any) => (
                  <span
                    className={`px-2.5 py-1 rounded-md text-[9px] font-black uppercase tracking-widest border ${
                      lb.status === "assessed"
                        ? "bg-green-50 text-green-600 border-green-100"
                        : "bg-gray-50 text-gray-400 border-gray-100"
                    }`}
                  >
                    {lb.status}
                  </span>
                ),
              },
              {
                key: "score",
                header: "Score",
                cell: (lb: any) => (
                  <span className="font-black text-sm text-gray-900">
                    {lb.total_score ?? "—"}
                  </span>
                ),
              },
              {
                key: "action",
                header: "",
                cell: (lb: any) => (
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => navigate(`/assessor/logbook/${lb.id}`)}
                      className="p-2 text-gray-400 hover:text-indigo-600 border border-transparent hover:border-indigo-100 hover:bg-indigo-50 rounded-xl transition-all"
                    >
                      <Eye className="w-4 h-4" />
                    </button>

                    <AccessGate perm={PERMS.logbook_diet.assign}>
                      <button
                        disabled={removing}
                        onClick={() => handleRemove(lb.id)}
                        className="p-2 text-gray-400 hover:text-red-600 border border-transparent hover:border-red-100 hover:bg-red-50 rounded-xl transition-all"
                      >
                        <UserMinus className="w-4 h-4" />
                      </button>
                    </AccessGate>
                  </div>
                ),
              },
            ]}
          />
        </div>

        {/* COMPLIANCE BANNER */}
        <div className="bg-gray-900 text-white rounded-[2.5rem] p-10 flex flex-col md:flex-row items-center gap-10 shadow-2xl shadow-gray-200 mt-10">
          <div className="w-20 h-20 bg-indigo-500/10 rounded-3xl flex items-center justify-center shrink-0 border border-indigo-500/20">
            <Award className="w-10 h-10 text-indigo-400" />
          </div>
          <div className="flex-1">
            <h4 className="text-[11px] font-black uppercase tracking-[0.4em] text-indigo-400 mb-3">
              Grading Compliance
            </h4>
            <p className="text-sm text-gray-400 leading-relaxed font-medium">
              Score entries must strictly adhere to the{" "}
              <span className="text-white">INSTITUTIONAL RUBRIC</span>. Status
              updates to <span className="text-white">FAIL</span> or{" "}
              <span className="text-white">ABSENT</span> will trigger automatic
              eligibility for the subsequent diet cycle registration.
            </p>
          </div>
        </div>
      </div>
    </AccessGate>
  );
}
